/**
 * useCustomerStatusMutation: E1: write a new customer status.
 *
 * live mode → PUT /customer-status/:subjectType/:subjectId, then invalidate the
 *             ['customers'] key family so BOTH surfaces refresh: the Kunden grid
 *             and the client page (which reads the same list endpoint and finds
 *             its row by subject_id).
 * mock mode → resolves null without a network call, mirroring useCreateTask.
 *
 * The api owns the rules (rule #2): 422 on an inaktiv subject, 403 without
 * 'customer_status.override'. This hook surfaces failures rather than judging them.
 *
 * [Source: apps/web/src/features/aufgaben/hooks/useCreateTask.ts] (live/mock split + invalidate)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import type { CustomerStatusResponse } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import type { KundeStatus } from '../../entity-detail/types';

export interface CustomerStatusChange {
  subjectType: 'person' | 'firma';
  subjectId: string;
  toStatus: KundeStatus;
  /**
   * Which route to use. 'transition' is the everyday path any user in the firm
   * may hold; 'override' bypasses the transition table and is admin-only. The
   * caller decides from the row's own allowed_transitions, so this file never
   * re-implements the table (rule #2).
   */
  mode: 'transition' | 'override';
}

export function useCustomerStatusMutation() {
  const qc = useQueryClient();
  const live = isLiveData();

  return useMutation<CustomerStatusResponse | null, Error, CustomerStatusChange>({
    mutationFn: ({ subjectType, subjectId, toStatus, mode }) => {
      if (!live) return Promise.resolve(null);
      return mode === 'override'
        ? apiClient.customerStatus.override(subjectType, subjectId, toStatus)
        : apiClient.customerStatus.transition(subjectType, subjectId, toStatus);
    },
    onSuccess: () => {
      // Covers qk.customers({...filters}) AND qk.customers({detail}): both are
      // ['customers','list', …]. The status change also moves the E4 since-date,
      // so a stale grid row would show the old date.
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}
